import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { CoreButton, ModalContext, type ModalContextType } from "@verity/components/core";
import type { ExemptedStudent } from "@verity/components";
import apiClient from "../../helpers/api-client";
import i18n from "../../i18n";
import { intIdToUuid } from "../../helpers/uuid";
import ProctoringResultView from "../../results/components/student-selection/videoplayer/ProctoringResultView";
import NeutralResultViewSkeleton from "../../results/components/student-selection/videoplayer/components/NeutralResultViewSkeleton";
import type { LiveSessionInfo } from "../../results/components/student-selection/videoplayer/ProctoringResultView";
import type { QuizInfoSummary, SessionInfo } from "../../results/components/TestResultsWrapper";
import { isLmsType } from "../../helpers/lms-name";

type TestTakerResultModalProps = {
	instituteId: number;
	courseId: number;
	assessmentId: number;
	/**
	 * The selected test taker is owned by the caller, which also decides whether this drawer renders at
	 * all. Keeping both in one place is what guarantees the selection can never go empty while
	 * ProctoringResultView is still mounted: it derives its attempt list — and from that its session uuid
	 * — from this id, and an empty attempt list sends it refetching against an undefined session uuid.
	 */
	selectedUserId: number;
	setSelectedUserId: (value: number | undefined) => void;
	studentList: { id: number; name: string; severityLevel?: number }[];
	/** Opens directly on this attempt instead of the default (latest) one for the selected test taker. */
	initialProctoringSessionId?: string;
	/**
	 * Called after an attempt has been deleted from inside the drawer. The drawer moves its own selection
	 * on by itself; this is for the page underneath, whose table rows and report figures still count the
	 * attempt that just went away.
	 */
	onAttemptDeleted?: () => void;
};

async function fetchLiveSessionsForAssessment({
	instituteUuid,
	courseUuid,
	assessmentId,
}: {
	instituteUuid: string;
	courseUuid: string;
	assessmentId: number;
}): Promise<Record<string, LiveSessionInfo>> {
	try {
		const liveSessions = await apiClient.liveClient.getAssessmentLiveSessionInfo({
			instituteUuid,
			courseUuid,
			assessmentUuid: intIdToUuid(assessmentId),
		});
		const instituteProctorGroups = await apiClient.liveClient.getLiveInstituteProctorGroup(instituteUuid);
		const proctorGroupsById = Object.fromEntries(
			instituteProctorGroups.map((proctorGroup) => [proctorGroup.id, proctorGroup]),
		);
		return Object.fromEntries(
			await Promise.all(
				liveSessions.results.map(async ({ id, name, isPopIn }) => {
					let proctorGroups: { id: number }[] = [];
					if (isPopIn) {
						proctorGroups = instituteProctorGroups;
					} else {
						try {
							const invitations = await apiClient.liveClient.getLiveSessionInvitations(id);
							proctorGroups = invitations
								.filter(({ proctorGroupId }) => proctorGroupsById[proctorGroupId] !== undefined)
								.map(({ proctorGroupId }) => ({
									...proctorGroupsById[proctorGroupId],
									id: proctorGroupId,
								}));
						} catch (e) {
							console.error(e);
						}
					}
					return [id, { id, name, isPopIn, proctorGroups }];
				}),
			),
		) as Record<string, LiveSessionInfo>;
	} catch (e) {
		console.error("Failed to get live session info", e);
		return {};
	}
}

// Rendered in a right-side drawer (rather than routed to its own page) so ProctoringResultView (built
// to be a standalone page in TestResultsWrapper) can be reused as-is from within the reports table.
export function TestTakerResultModal({
	instituteId,
	courseId,
	assessmentId,
	selectedUserId,
	setSelectedUserId,
	studentList,
	initialProctoringSessionId,
	onAttemptDeleted,
}: TestTakerResultModalProps) {
	// Local open state (rather than tying the drawer straight to this component's mount/unmount) so the
	// close transition can finish playing before the parent drops us — see beginClose/handleClosed below.
	const [drawerOpen, setDrawerOpen] = useState(true);

	// Which attempt to open on. Seeded from the prop, then owned locally so deleting an attempt can point
	// the view at the one that takes its place — ProctoringResultView picks its own attempt from a changed
	// attempt list otherwise, and "the next one" is this component's decision to make, not its.
	const [openOnSessionId, setOpenOnSessionId] = useState(initialProctoringSessionId);
	useEffect(() => {
		setOpenOnSessionId(initialProctoringSessionId);
	}, [initialProctoringSessionId]);

	const [assessmentInfo, setAssessmentInfo] = useState<QuizInfoSummary | null>(null);
	const [liveSessions, setLiveSessions] = useState<Record<string, LiveSessionInfo>>({});
	const [exemptedStudents, setExemptedStudents] = useState<Omit<ExemptedStudent, "name">[]>([]);
	const [sessionsByStudent, setSessionsByStudent] = useState<Map<number, SessionInfo[]>>(new Map());
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			const [institute, course, assessment] = await Promise.all([
				apiClient.institute.get(instituteId),
				apiClient.course.getCourseInfoFromId(courseId),
				apiClient.assessment.getAssessmentByAssessmentId(assessmentId),
			]);
			if (cancelled) return;
			if (!assessment) {
				setLoadError(true);
				setLoading(false);
				return;
			}

			const lms = isLmsType(institute?.lms) ? institute.lms : undefined;
			const quizName =
				typeof assessment.quizInfo === "object" && assessment.quizInfo !== null && "name" in assessment.quizInfo
					? assessment.quizInfo.name
					: undefined;

			const [sessionList, exempted, liveSessionMap] = await Promise.all([
				apiClient.proctoringSession.getSessionResultList(assessment.quizId, courseId).catch(() => []),
				course.courseId
					? apiClient.user
							.getProctoringExemptionStudents({ courseId: course.courseId, quizId: assessment.quizId })
							.catch(() => [])
					: Promise.resolve([]),
				institute?.uuid && course.accessControlUuid
					? fetchLiveSessionsForAssessment({
							instituteUuid: institute.uuid,
							courseUuid: course.accessControlUuid,
							assessmentId: assessment.id,
						})
					: Promise.resolve({}),
			]);
			if (cancelled) return;

			const byStudent = new Map<number, SessionInfo[]>();
			for (const session of sessionList ?? []) {
				const arr = byStudent.get(session.studentId) ?? [];
				arr.push(session);
				byStudent.set(session.studentId, arr);
			}

			setAssessmentInfo({
				id: assessment.id,
				lmsQuizId: assessment.quizId,
				name: quizName ?? (i18n.t("result.components.resultsPage.testResultsWrapperTSX.unamedAssessment") as string),
				lms,
				isBlackboardUltra: course.isBlackboardUltra ?? undefined,
			});
			setSessionsByStudent(byStudent);
			setExemptedStudents(exempted);
			setLiveSessions(liveSessionMap);
			setLoading(false);
		};

		load().catch((e) => {
			console.error(e);
			if (!cancelled) {
				setLoadError(true);
				setLoading(false);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [instituteId, courseId, assessmentId]);

	const proctoringSessions = sessionsByStudent.get(selectedUserId) ?? [];

	// Closing is two-phase, and the phases must stay separate. beginClose only plays the slide-out; the
	// parent drops us afterwards, from the transition's onExited. Clearing selectedUserId up front would
	// unmount us mid-transition (no animation) and, worse, hand ProctoringResultView an empty attempt
	// list while it is still on screen.
	const beginClose = () => {
		setDrawerOpen(false);
	};

	// CoreModal re-homes itself into the portal shell's own in-page container (.modalContainer in
	// portal.module.css, z-index 1001) whenever useMobileLayout says the viewport is narrow — the same flag
	// that switches the nav rail to its always-expanded form, which is why both misbehave at one width.
	// That container lives inside the page, underneath this drawer, so every dialog the result view opens
	// (add event, resolve event, delete recording) rendered behind it and couldn't be reached. A container
	// ref that stays null sends them to document.body instead, above the drawer, at any width.
	const detachedModalContainer = useRef<HTMLDivElement>(null);
	const modalContext = useMemo<ModalContextType>(() => ({ modalContainer: detachedModalContainer }), []);

	const handleClosed = () => {
		setSelectedUserId(undefined);
	};

	/**
	 * Where the drawer lands once an attempt has been deleted, in the order the feature calls for: the
	 * test-taker's next remaining attempt, else the next test-taker who still has one, else nothing is
	 * left to show and the drawer closes.
	 *
	 * The deleted attempt is dropped from `sessionsByStudent` here rather than refetched — the list this
	 * drawer was opened with is otherwise unchanged, and a refetch would flash the whole view through its
	 * loading skeleton for a row that is already known to be gone.
	 */
	const handleAttemptDeleted = (deletedSessionUuid: string) => {
		const remaining = proctoringSessions.filter(({ uuid }) => uuid !== deletedSessionUuid);

		// The page behind this drawer counted the deleted attempt in its own rows and figures, and it reads
		// them from the server rather than from anything shared with us, so it has to go and look again.
		onAttemptDeleted?.();

		setSessionsByStudent((previous) => {
			const next = new Map(previous);
			if (remaining.length > 0) {
				next.set(selectedUserId, remaining);
			} else {
				next.delete(selectedUserId);
			}
			return next;
		});

		if (remaining.length > 0) {
			// The attempt after the deleted one, or the new last attempt when it was the last.
			const deletedIndex = proctoringSessions.findIndex(({ uuid }) => uuid === deletedSessionUuid);
			setOpenOnSessionId(remaining[Math.min(Math.max(deletedIndex, 0), remaining.length - 1)]?.uuid);
			return;
		}

		// That test-taker has no attempts left. Walk the roster from where we are for the next one who
		// still does — wrapping, and skipping the test-taker we just emptied.
		const currentIndex = studentList.findIndex(({ id }) => id === selectedUserId);
		const nextStudent = studentList
			.slice(currentIndex + 1)
			.concat(studentList.slice(0, Math.max(currentIndex, 0)))
			.find(({ id }) => id !== selectedUserId && (sessionsByStudent.get(id)?.length ?? 0) > 0);

		setOpenOnSessionId(undefined);
		if (nextStudent) {
			setSelectedUserId(nextStudent.id);
		} else {
			beginClose();
		}
	};

	return (
		<Drawer
			anchor="right"
			open={drawerOpen}
			onClose={beginClose}
			SlideProps={{ appear: true, onExited: handleClosed }}
			// Below the modal layer (theme.zIndex.modal, 1300) rather than level with it: the dialogs the
			// result view opens are CoreModals sitting at exactly 1300, and a tie leaves which one paints on
			// top to DOM order. Belt and braces next to the ModalContext override below, which is what
			// actually got them out from behind this drawer. Still above the page and its app bar, so the
			// drawer covers what it always did.
			sx={{ zIndex: (theme) => theme.zIndex.modal - 50 }}
			PaperProps={{ sx: { width: "95vw", maxWidth: "100%" } }}
		>
			<ModalContext.Provider value={modalContext}>
				<Box
					sx={{
						width: "100%",
						height: "100%",
						background: "#F7F8FA",
						overflow: "auto",
					}}
				>
					{/* The same placeholder the result view falls back to while it works out the assessment type,
					    so the handover from this branch to that one is invisible. Not the view itself: it derives
					    its selected attempt from `proctoringSessions`, which is still empty here. */}
					{loading ? (
						<NeutralResultViewSkeleton />
					) : loadError || !assessmentInfo || sessionsByStudent.size === 0 || proctoringSessions.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "16px",
								width: "100%",
								height: "100%",
							}}
						>
							<Typography fontWeight={500} fontSize="15px">
								{i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.noSessionsFound")}
							</Typography>
							<CoreButton onClick={beginClose}>
								{i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.close")}
							</CoreButton>
						</Box>
					) : (
						<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", width: "100%", minHeight: "100%" }}>
							<ProctoringResultView
								instituteId={instituteId}
								courseId={courseId}
								showVideo
								proctoringSessions={proctoringSessions}
								liveSessions={liveSessions}
								setShowStudentResults={(show) => {
									if (!show) beginClose();
								}}
								assessmentInfo={assessmentInfo}
								selectedStudent={selectedUserId}
								setSelectedStudent={setSelectedUserId}
								studentList={studentList}
								exemptedStudents={exemptedStudents}
								initialProctoringSessionId={openOnSessionId}
								onAttemptDeleted={handleAttemptDeleted}
							/>
						</Box>
					)}
				</Box>
			</ModalContext.Provider>
		</Drawer>
	);
}

export default TestTakerResultModal;
